import Api from './Api';
import { EnvVars } from '../constants/EnvConfig';
import {
  DescribeSiteResponse,
  GetEventAvailabilityResponse,
  GetSitesWithinRadiusParams,
  GetSitesWithinRadiusResponse,
  SearchAllSitesParams,
  SearchAllSitesResponse,
} from '../types/Site';

// The For Neighbors RPC handler lives at the bare domain root, not under apiUrl's /api/3.0/ REST
// namespace -- new URL('/', apiUrl) resolves to exactly `${origin}/` regardless of apiUrl's path,
// which Api.post/get's resolveUrl() then uses as-is (it's an absolute URL) instead of prepending
// apiUrl. Confirmed against a working curl to the deployed dev backend's domain root.
//
// Computed lazily (not at module load) because this resolution only holds for the spec-compliant
// URL Expo SDK 53 installs in place of React Native's own stub -- resolving it at import time
// would make correctness depend on module evaluation order rather than on when it's actually used.
const { apiUrl } = EnvVars;
let forNeighborsUrl: string | undefined;
function getForNeighborsUrl(): string {
  if (!forNeighborsUrl) forNeighborsUrl = new URL('/', apiUrl).toString();
  return forNeighborsUrl;
}

function forNeighborsHeaders(operation: string): Record<string, string> {
  return {
    'Content-Type': 'application/x-amz-json-1.1',
    'X-Amz-Target': `ForNeighbours.${operation}`,
  };
}

// The spec states none of these four operations require a signed-in session, so needsSession
// defaults to false -- unlike Api.post's own default, an anonymous neighbor browsing the map must
// not be forced into establishing one.
export async function getSitesWithinRadius(
  params: GetSitesWithinRadiusParams,
  needsSession: boolean = false
): Promise<GetSitesWithinRadiusResponse> {
  return Api.post(
    getForNeighborsUrl(),
    params,
    forNeighborsHeaders('GetSitesWithinRadius'),
    'POST',
    needsSession
  );
}

// Addressed by id alone: DescribeSite takes no origin the way GetSitesWithinRadius does, so its
// Site carries no distance -- that field lives on the radius search's own result envelope, since a
// distance is a property of the query rather than of the place. The details screen computes its own
// neighbor-relative distance instead; see useSite.ts's distanceMiles.
export async function describeSite(
  siteId: string,
  needsSession: boolean = false
): Promise<DescribeSiteResponse> {
  return Api.post(
    getForNeighborsUrl(),
    { siteId },
    forNeighborsHeaders('DescribeSite'),
    'POST',
    needsSession
  );
}

export async function searchAllSites(
  params: SearchAllSitesParams,
  needsSession: boolean = false
): Promise<SearchAllSitesResponse> {
  return Api.post(getForNeighborsUrl(), params, forNeighborsHeaders('SearchAllSites'), 'POST', needsSession);
}

// Anonymous neighbors can view availability without a session, same reasoning as describeSite.
export async function getEventAvailability(
  occurrenceId: string,
  needsSession: boolean = false
): Promise<GetEventAvailabilityResponse> {
  return Api.post(
    getForNeighborsUrl(),
    { occurrenceId },
    forNeighborsHeaders('GetEventAvailability'),
    'POST',
    needsSession
  );
}
